import {
  Controller,
  useFormContext,
  UseFormSetValue,
  UseFormGetValues,
} from "react-hook-form"; // Assuming you're using react-hook-form
import Select from "react-select";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import { State } from "country-state-city";
import React, {
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";

import Cookies from "js-cookie";
import { FieldError, OptionalHint } from "./FieldMessages";
interface BillingAddressFormProps {
  control: any; // Replace with the type for your form control
  errors: any;
  setValue: any;
  clearErrors: any;
  namespace: any;
  billingCity: any;
  billingAddressInfo: any;
  billingZip: any;
  billingState: any;
  billingCountry: any;
  // setFormValue:Dispatch<SetStateAction<any>>
}

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  control,
  errors,
  setValue,
  billingCity,
  billingAddressInfo,
  clearErrors,
  billingZip,
  billingState,
  billingCountry,
  namespace,
}) => {
  const fid = (name: string) => `${namespace}-${name.replace(/\./g, "-")}`;
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = Cookies.get("theme");
  const darkForm =
    isDark === "dark" || (isDarkMode === true && isDark === "system");

  const formatOptionLabel = ({ label, value }: any, { context }: any) => {
    return context === "value" ? value : label;
  };
  const stateOption = useMemo(() => {
    return State.getAllStates()
      .filter((code: any) => code.countryCode === "US")
      .map((state: any) => ({
        label: state.name,
        value: state.isoCode,
      }));
  }, []);
  const [formValue, setFormValue] = useState({});
  const hanldeChange = (e: any) => {
    const { name, value } = e?.target;
    setFormValue((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    Cookies.set(
      "BillingAddress",
      JSON.stringify({ ...formValue, [name]: value })
    );
  };
  useEffect(() => {
    if (billingCity) {
      setValue(`${namespace}.city`, billingCity);
    }
    if (billingAddressInfo) {
      setValue(`${namespace}.address1`, billingAddressInfo);
    }
    if (billingZip) {
      setValue(`${namespace}.zip`, billingZip);
    }
    if (billingState) {
      setValue(`${namespace}.state`, billingState);
    }
    if (billingCountry) {
      setValue(`${namespace}.country`, billingCountry);
    }
  }, [
    billingCity,
    control,
    namespace,
    billingAddressInfo,
    billingZip,
    billingState,
    billingCountry,
  ]);
  return (
    <div className="mt-6 mb-4 space-y-3 sm:space-y-5">
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
        Billing Address
      </h1>
      <div className="max-w-lg">
        <Label htmlFor={fid("address1")} className="text-sm">
          <span className="text-red-500" aria-hidden>
            *
          </span>{" "}
          Address 1
          <span className="sr-only"> (required)</span>
        </Label>
        <Controller
          name={`${namespace}.address1`}
          control={control}
          rules={{
            required: "Street address is required",
          }}
          render={({ field: { ref, ...field } }) => {
            const err = errors[`${namespace}`]?.address1;
            return (
              <Input
                {...field}
                ref={ref}
                id={fid("address1")}
                autoComplete="billing street-address"
                className="mt-1.5"
                placeholder="Street address"
                aria-invalid={!!err}
                aria-describedby={err ? `${fid("address1")}-error` : undefined}
                onInput={(e: any) => hanldeChange(e)}
              />
            );
          }}
        />
        <FieldError
          id={`${fid("address1")}-error`}
          message={errors[`${namespace}`]?.address1?.message as string}
        />
      </div>
      <div className="max-w-lg">
        <Label htmlFor={fid("address2")} className="text-sm">
          Address 2
          <OptionalHint />
        </Label>
        <Controller
          name={`${namespace}.address2`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              ref={ref}
              id={fid("address2")}
              autoComplete="billing address-line2"
              className="mt-1.5"
              placeholder="Apartment, suite, etc."
              onInput={(e: any) => hanldeChange(e)}
            />
          )}
        />
      </div>
      <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="flex-1">
          <Label htmlFor={fid("city")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>{" "}
            City
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.city`}
            control={control}
            rules={{
              required: "City is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.city;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("city")}
                  autoComplete="billing address-level2"
                  className="mt-1.5"
                  placeholder="City"
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("city")}-error` : undefined}
                  onInput={(e: any) => hanldeChange(e)}
                />
              );
            }}
          />
          <FieldError
            id={`${fid("city")}-error`}
            message={errors[`${namespace}`]?.city?.message as string}
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor={`${fid("state")}-input`}
            className="text-sm text-slate-700 dark:text-slate-200"
          >
            <span className="text-red-500" aria-hidden>
              *
            </span>{" "}
            State
            <span className="sr-only"> (required)</span>
          </label>
          <Controller
            name={`${namespace}.state`}
            control={control}
            rules={{
              required: "State is required",
            }}
            render={({ field: { onChange, onBlur, value, ref } }) => {
              const err = errors[`${namespace}`]?.state;
              return (
              <Select
                ref={ref}
                inputId={`${fid("state")}-input`}
                className={`mt-1.5 input-focus-disable ${err ? "rounded-md ring-2 ring-red-500 ring-offset-0" : ""}`}
                options={stateOption}
                isSearchable
                value={stateOption.find((option) => option.value === value)}
                onChange={(selectedOption, e) => {
                  onChange(selectedOption ? selectedOption.value : "");
                  clearErrors(`${namespace}.state` as any);
                  hanldeChange({
                    target: {
                      name: "payment.state",
                      value: selectedOption?.value,
                    },
                  });
                }}
                onBlur={onBlur}
                aria-invalid={!!err}
                aria-errormessage={err ? `${fid("state")}-error` : undefined}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: darkForm ? "rgb(30 41 59)" : "#ffffff",
                    borderColor: darkForm ? "rgb(71 85 105)" : provided.borderColor,
                    height: "48px",
                    overflow: "auto",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: darkForm ? "rgb(30 41 59)" : "#ffffff",
                    border: darkForm ? "1px solid rgb(71 85 105)" : undefined,
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: darkForm
                      ? state.isFocused
                        ? "rgb(51 65 85)"
                        : "rgb(30 41 59)"
                      : state.isFocused
                      ? "#DEEBFF"
                      : "#ffffff",
                    color: darkForm
                      ? "rgb(241 245 249)"
                      : state.isSelected
                      ? "#000000"
                      : "#333333",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: darkForm ? "rgb(241 245 249)" : "#000000",
                  }),
                }}
                formatOptionLabel={formatOptionLabel}
              />
              );
            }}
          />
          <FieldError
            id={`${fid("state")}-error`}
            message={errors[`${namespace}`]?.state?.message as string}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="flex-1">
          <Label htmlFor={fid("country")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>{" "}
            Country
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.country`}
            control={control}
            rules={{
              required: "Country is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.country;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("country")}
                  autoComplete="billing country-name"
                  className="mt-1.5"
                  placeholder="Country"
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("country")}-error` : undefined}
                  onInput={(e: any) => hanldeChange(e)}
                />
              );
            }}
          />
          <FieldError
            id={`${fid("country")}-error`}
            message={errors[`${namespace}`]?.country?.message as string}
          />
        </div>
        <div className="flex-1">
          <Label htmlFor={fid("zip")} className="text-sm">
            <span className="text-red-500" aria-hidden>
              *
            </span>{" "}
            ZIP code
            <span className="sr-only"> (required)</span>
          </Label>
          <Controller
            name={`${namespace}.zip`}
            control={control}
            rules={{
              required: "ZIP code is required",
            }}
            render={({ field: { ref, ...field } }) => {
              const err = errors[`${namespace}`]?.zip;
              return (
                <Input
                  {...field}
                  ref={ref}
                  id={fid("zip")}
                  autoComplete="billing postal-code"
                  inputMode="numeric"
                  className="mt-1.5"
                  placeholder="ZIP code"
                  aria-invalid={!!err}
                  aria-describedby={err ? `${fid("zip")}-error` : undefined}
                  onInput={(e: any) => {
                    e.target.value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);
                    hanldeChange(e);
                  }}
                />
              );
            }}
          />
          <FieldError
            id={`${fid("zip")}-error`}
            message={errors[`${namespace}`]?.zip?.message as string}
          />
        </div>
      </div>
    </div>
  );
};
export default BillingAddressForm;
